// src/views/GetStarted.jsx
import React, { useState, useMemo, useEffect } from 'react';
import {
  SiInstagram,
  SiTiktok,
  SiYoutube,
  SiFacebook,
  SiLinktree, // example for "Other"
  SiSnapchat,
} from 'react-icons/si';
import styles from '../styles/GetStarted.module.css';

export default function GetStarted() {
  // ─── Check URL parameters for referral ──────────────────────
  const [referralUsername, setReferralUsername] = useState(null);
  
  // ─── Master form state ───────────────────────────────────────
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    services: { facebook: false, snapchat: false },
    hasFbPage: '',                // 'Yes' or 'No'
    fbPageName: '',
    fbUsernames: [''],
    contentSources: [{ platform: 'Instagram', handle: '' }],
    promoLink: '',
    autoResponse: '',
    longForm: { yt: false, pod: false, live: false, none: false },
    shortLib: '',
    revenue: '',
    hasExtraContent: '',          // 'Yes' or 'No'
    extraContentLink: '',
    businessName: '',
    businessEmail: '',
    isReferral: '',               // 'Yes' or 'No'
    referredBy: '',               // Name of person who referred
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const API_URL = 'https://sucrbe1r7d.execute-api.us-east-1.amazonaws.com/Prod/onboarding';
  const AFFILIATE_API_URL = 'https://rody753ay0.execute-api.us-east-1.amazonaws.com/dev';

  // ─── Check URL parameters on component mount ────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referral = urlParams.get('r');
    
    if (referral && referral.trim()) {
      setReferralUsername(referral.trim());
      // Pre-fill the referral form fields
      setForm(prev => ({
        ...prev,
        isReferral: 'Yes',
        referredBy: referral.trim()
      }));
    }
  }, []);

  // ─── Countdown effect ────────────────────────────────────────────────
  useEffect(() => {
    if (submitted && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (submitted && countdown === 0) {
      // Redirect to home page
      window.location.href = '/';
    }
  }, [submitted, countdown]);

  // ─── Handlers ─────────────────────────────────────────────────────────
  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const updateNested = (group, key, value) => {
    setForm(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: value },
    }));
  };

  const updateFbUsername = (idx, value) => {
    setForm(prev => {
      const arr = [...prev.fbUsernames];
      arr[idx] = value;
      return { ...prev, fbUsernames: arr };
    });
  };

  const addFbUsername = () => {
    setForm(prev => ({ ...prev, fbUsernames: [...prev.fbUsernames, ''] }));
  };

  const removeFbUsername = idx => {
    setForm(prev => {
      const arr = [...prev.fbUsernames];
      arr.splice(idx, 1);
      return { ...prev, fbUsernames: arr };
    });
  };

  const updateContentSource = (idx, key, value) => {
    setForm(prev => {
      const arr = prev.contentSources.map((entry, i) =>
        i === idx ? { ...entry, [key]: value } : entry
      );
      return { ...prev, contentSources: arr };
    });
  };

  const addContentSource = () => {
    setForm(prev => ({
      ...prev,
      contentSources: [...prev.contentSources, { platform: 'Instagram', handle: '' }],
    }));
  };

  const removeContentSource = idx => {
    setForm(prev => {
      const arr = [...prev.contentSources];
      arr.splice(idx, 1);
      return { ...prev, contentSources: arr };
    });
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      // First, submit the main onboarding form
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save data');
      }
      const data = await response.json();

      // If this is a referral, add to affiliate system
      if (referralUsername) {
        try {
          // Determine Facebook page name to use
          let facebookPageName = '';
          if (form.hasFbPage === 'Yes' && form.fbPageName.trim()) {
            facebookPageName = form.fbPageName.trim();
          } else if (form.name.trim()) {
            // Use person's name if no Facebook page name specified
            facebookPageName = `${form.name.trim()}'s Page`;
          } else {
            facebookPageName = 'New Customer Page';
          }

          // Call affiliate API to add referral
          const affiliateResponse = await fetch(
            `${AFFILIATE_API_URL}/affiliate/username/${referralUsername}/referral`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                facebook_page_name: facebookPageName
              }),
            }
          );

          if (!affiliateResponse.ok) {
            // Log the error but don't fail the whole submission
            console.warn('Failed to add affiliate referral:', await affiliateResponse.text());
          } else {
            console.log('Successfully added affiliate referral');
          }
        } catch (affiliateError) {
          // Log the error but don't fail the whole submission
          console.warn('Error adding affiliate referral:', affiliateError);
        }
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // ─── map platform key → icon component ────────────────────────────────
  const platformIconMap = {
    Instagram: <SiInstagram size={20} />,
    TikTok: <SiTiktok size={20} />,
    YouTube: <SiYoutube size={20} />,
    Facebook: <SiFacebook size={20} />,
    Other: <SiLinktree size={20} />, // you can swap linktree icon for a generic link icon
  };

  // ─── Step definitions with validation ─────────────────────────────────
  const allSteps = useMemo(
    () => [
      // 0. Contact Info
      {
        id: 'contactInfo',
        isComplete: () =>
          form.name.trim() !== '' && (form.phone.trim() !== '' || form.email.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="contactInfo">
            <legend>
              Your Contact Information<span className={styles.required}>*</span>
            </legend>
            <label>
              Your Name:
              <input
                className={styles.input}
                placeholder="Full Name"
                value={form.name}
                onChange={e => updateField('name', e.target.value.trimStart())}
              />
            </label>
            <label>
              Phone Number:
              <input
                type="tel"
                className={styles.input}
                placeholder="e.g. 123-456-7890"
                value={form.phone}
                onChange={e => updateField('phone', e.target.value.trim())}
              />
            </label>
            <label>
              Email Address:
              <input
                type="email"
                className={styles.input}
                placeholder="e.g. you@example.com"
                value={form.email}
                onChange={e => updateField('email', e.target.value.trim())}
              />
            </label>
          </fieldset>
        ),
      },
      // 1. Select Service
      {
        id: 'services',
        isComplete: () => form.services.facebook || form.services.snapchat,
        render: () => {
          const options = [
            {
              id: 'facebook',
              label: 'Facebook Page Management',
              description: 'We\'ll help manage and grow your Facebook presence',
              icon: <SiFacebook size={24} />
            },
            {
              id: 'snapchat',
              label: 'Snapchat Star Acquisition',
              description: 'We\'ll help you become a Snapchat Star',
              icon: <SiSnapchat size={24} />
            }
          ];
          
          const handleServiceSelect = (serviceId) => {
            // Ensure only one service can be selected at a time
            const newServices = {
              facebook: false,
              snapchat: false
            };
            newServices[serviceId] = true;
            
            setForm(prev => ({
              ...prev,
              services: newServices
            }));
          };
          
          return (
            <fieldset className={styles.step} key="services">
              <legend>
                Select Service<span className={styles.required}>*</span>
              </legend>
              <div className={styles.serviceOptions}>
                {options.map(opt => {
                  const isSelected = form.services[opt.id];
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`${styles.serviceOption} ${
                        isSelected ? styles.selected : ''
                      }`}
                      onClick={() => handleServiceSelect(opt.id)}
                      aria-pressed={isSelected}
                    >
                      <div className={styles.serviceIcon}>
                        {opt.icon}
                      </div>
                      <div className={styles.serviceContent}>
                        <div className={styles.serviceLabel}>{opt.label}</div>
                        <div className={styles.serviceDescription}>{opt.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        },
      },
      // 2. Do you already have a Facebook Page?
      {
        id: 'hasFbPage',
        isComplete: () => form.hasFbPage === 'Yes' || form.hasFbPage === 'No',
        render: () => (
          <fieldset className={styles.step} key="hasFbPage">
            <legend>
              Do you already have a Facebook Page?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.hasFbPage}
              onChange={val => updateField('hasFbPage', val)}
            />
          </fieldset>
        ),
      },
      // 3. Facebook Page Name (if hasFbPage === 'Yes')
      {
        id: 'fbPageName',
        condition: () => form.hasFbPage === 'Yes',
        isComplete: () => form.fbPageName.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="fbPageName">
            <legend>
              Facebook Page Name (same as stage name)<span className={styles.required}>*</span>
            </legend>
            <input
              className={styles.input}
              placeholder="Exact Page Name"
              value={form.fbPageName}
              onChange={e => updateField('fbPageName', e.target.value.trimStart())}
            />
          </fieldset>
        ),
      },
      // 4. Facebook Username(s) (if hasFbPage === 'Yes')
      {
        id: 'fbUsernames',
        condition: () => form.hasFbPage === 'Yes',
        isComplete: () => form.fbUsernames.every(u => u.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="fbUsernames">
            <legend>
              Facebook Username(s) <br />
              <span className={styles.hintSmall}>
                (Facebook.com/username) <br />
                <em>add more if taken</em><span className={styles.required}>*</span>
              </span>
            </legend>
            {form.fbUsernames.map((u, idx) => (
              <div key={idx} className={styles.platformRow}>
                <input
                  className={styles.inputHalf}
                  placeholder="Username (e.g. yourname)"
                  value={u}
                  onChange={e => updateFbUsername(idx, e.target.value.trim())}
                />
                {form.fbUsernames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFbUsername(idx)}
                    className={styles.removeBtn}
                    aria-label="Remove this username"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addFbUsername}
              className={styles.smallLink}
            >
              + Add another username
            </button>
          </fieldset>
        ),
      },
      // 5. Where can we pull content from?
      {
        id: 'contentSources',
        isComplete: () =>
          form.contentSources.length > 0 &&
          form.contentSources.every(cs => cs.handle.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="contentSources">
            <legend>
              Where can we pull content from? <br />
              <span className={styles.hintSmall}>
                (Instagram, TikTok, YouTube, etc.)<span className={styles.required}>*</span>
              </span>
            </legend>
            <div className={styles.platformList}>
              {form.contentSources.map((entry, idx) => (
                <div key={idx} className={styles.platformRow}>
                  <div className={styles.platformIconContainer}>
                    {platformIconMap[entry.platform]}
                  </div>
                  <select
                    className={styles.platformSelect}
                    value={entry.platform}
                    onChange={e =>
                      updateContentSource(idx, 'platform', e.target.value)
                    }
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    className={styles.inputInline}
                    placeholder={`${entry.platform} handle`}
                    value={entry.handle}
                    onChange={e =>
                      updateContentSource(idx, 'handle', e.target.value.trim())
                    }
                  />
                  {form.contentSources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContentSource(idx)}
                      className={styles.removeBtn}
                      aria-label="Remove this platform entry"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addContentSource}
              className={styles.smallLink}
            >
              <span className={styles.plusIcon}>+</span>
              <span>Add another platform</span>
            </button>
          </fieldset>
        ),
      },
      // 6. Link to Promote
      {
        id: 'promoLink',
        isComplete: () => form.promoLink.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="promoLink">
            <legend>
              Link to Promote <br />
              <span className={styles.hintSmall}>
                (Linktree, hoo.be, snipfeed, personal domain, etc.)<span className={styles.required}>*</span>
              </span>
            </legend>
            <input
              className={styles.input}
              placeholder="Full URL"
              value={form.promoLink}
              onChange={e => updateField('promoLink', e.target.value.trim())}
            />
          </fieldset>
        ),
      },
      // 7. Automated Response (optional)
      {
        id: 'autoResponse',
        isComplete: () => true,
        render: () => (
          <fieldset className={styles.step} key="autoResponse">
            <legend>
              Automated messages are great for conversions.<br />
              Do you have an auto response to add?<br />
              <span className={styles.hintSmall}>
                e.g. "I don't always answer here… link in my bio!"
              </span>
            </legend>
            <textarea
              className={styles.textarea}
              placeholder="Paste your auto-reply here"
              value={form.autoResponse}
              onChange={e => updateField('autoResponse', e.target.value)}
            />
          </fieldset>
        ),
      },
      // 8. Long-form content produced?
      {
        id: 'longForm',
        isComplete: () =>
          form.longForm.yt ||
          form.longForm.pod ||
          form.longForm.live ||
          form.longForm.none,
        render: () => {
          const opts = [
            { key: 'yt', label: 'YouTube' },
            { key: 'pod', label: 'Podcasts' },
            { key: 'live', label: 'Live Streams' },
            { key: 'none', label: 'None of the Above' },
          ];
          return (
            <fieldset className={styles.step} key="longForm">
              <legend>
                Do you create any of the following long-form content?<span className={styles.required}>*</span>
              </legend>
              <div className={styles.optionGroupMulti}>
                {opts.map(opt => {
                  const isSelected = form.longForm[opt.key];
                  return (
                    <div
                      key={opt.key}
                      className={`${styles.toggleButton} ${
                        isSelected ? styles.toggleSelected : ''
                      }`}
                      onClick={() => {
                        if (opt.key === 'none') {
                          setForm(prev => ({
                            ...prev,
                            longForm: {
                              yt: false,
                              pod: false,
                              live: false,
                              none: !prev.longForm.none,
                            },
                          }));
                        } else {
                          setForm(prev => ({
                            ...prev,
                            longForm: {
                              ...prev.longForm,
                              [opt.key]: !prev.longForm[opt.key],
                              none: false,
                            },
                          }));
                        }
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          );
        },
      },
      // 9. Short-form library?
      {
        id: 'shortLib',
        isComplete: () => form.shortLib !== '',
        render: () => (
          <fieldset className={styles.step} key="shortLib">
            <legend>
              Do you have a short-form content library to repurpose?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={[
                "Yes – It's large & consistently growing",
                "Yes – It's decent but not consistent",
                'Not really',
              ]}
              selected={form.shortLib}
              onChange={val => updateField('shortLib', val)}
            />
          </fieldset>
        ),
      },
      // 10. Average monthly revenue
      {
        id: 'revenue',
        isComplete: () => form.revenue !== '',
        render: () => (
          <fieldset className={styles.step} key="revenue">
            <legend>
              What's your average monthly revenue across platforms?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['$0-$10k', '$10k-$50k', '$50k-$100k', '$100k+']}
              selected={form.revenue}
              onChange={val => updateField('revenue', val)}
            />
          </fieldset>
        ),
      },
      // 11. Extra content?
      {
        id: 'hasExtraContent',
        isComplete: () => form.hasExtraContent === 'Yes' || form.hasExtraContent === 'No',
        render: () => (
          <fieldset className={styles.step} key="hasExtraContent">
            <legend>
              Do you have a Dropbox/shared album/drive with extra content?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.hasExtraContent}
              onChange={val => updateField('hasExtraContent', val)}
            />
          </fieldset>
        ),
      },
      // 12. Extra content link (if applicable)
      {
        id: 'extraContentLink',
        condition: () => form.hasExtraContent === 'Yes',
        isComplete: () => true,
        render: () => (
          <fieldset className={styles.step} key="extraContentLink">
            <legend>
              Link to extra content/SFW pictures
            </legend>
            <input
              className={styles.input}
              placeholder="Dropbox/Google Drive URL"
              value={form.extraContentLink}
              onChange={e => updateField('extraContentLink', e.target.value.trim())}
            />
          </fieldset>
        ),
      },
      // 13. Business Info
      {
        id: 'businessInfo',
        isComplete: () => true,
        render: () => (
          <fieldset className={styles.step} key="businessInfo">
            <legend>Business Information (optional)</legend>
            <label>
              Business Name:
              <input
                className={styles.input}
                placeholder="e.g. MyBrand Inc."
                value={form.businessName}
                onChange={e => updateField('businessName', e.target.value.trimStart())}
              />
            </label>
            <label>
              Business Email:
              <input
                type="email"
                className={styles.input}
                placeholder="e.g. info@mybrand.com"
                value={form.businessEmail}
                onChange={e => updateField('businessEmail', e.target.value.trim())}
              />
            </label>
          </fieldset>
        ),
      },
      // 14. Referral Question (hidden if referral parameter is present)
      {
        id: 'referral',
        condition: () => !referralUsername, // Hide this step if we have a referral parameter
        isComplete: () => form.isReferral === 'Yes' ? form.referredBy.trim() !== '' : form.isReferral === 'No',
        render: () => (
          <fieldset className={styles.step} key="referral">
            <legend>
              Was this a referral?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.isReferral}
              onChange={val => updateField('isReferral', val)}
            />
            {form.isReferral === 'Yes' && (
              <div className={styles.referralField}>
                <label>
                  Referred by:
                  <input
                    className={styles.input}
                    placeholder="Name of person who referred you"
                    value={form.referredBy}
                    onChange={e => updateField('referredBy', e.target.value.trimStart())}
                  />
                </label>
              </div>
            )}
          </fieldset>
        ),
      },
    ],
    [form, referralUsername]
  );

  // ─── Filter steps based on condition ─────────────────────────────────
  const visibleSteps = useMemo(
    () => allSteps.filter(step => !step.condition || step.condition()),
    [allSteps]
  );

  // ─── OptionGroup Component ─────────────────────────────────────────────
  const OptionGroup = ({ options, selected, onChange, singleSelect = true }) => {
    return (
      <div className={styles.optionGroup}>
        {options.map(opt => {
          const isSelected = singleSelect
            ? selected === opt
            : selected.includes(opt);
          return (
            <div
              key={opt}
              className={`${styles.optionButton} ${
                isSelected ? styles.selected : ''
              }`}
              onClick={() => {
                if (singleSelect) {
                  onChange(opt);
                } else {
                  if (isSelected) {
                    onChange(selected.filter(item => item !== opt));
                  } else {
                    onChange([...selected, opt]);
                  }
                }
              }}
            >
              {opt}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Thank You Screen ────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className={styles.container}>
        <div className={styles.thankYouWrapper}>
          <div className={styles.thankYouContent}>
            <div className={styles.thankYouIcon}>✓</div>
            <h1 className={styles.thankYouTitle}>Success!</h1>
            <p className={styles.thankYouMessage}>
              Form has been successfully submitted.
            </p>
            <div className={styles.welcomeSection}>
              <h2 className={styles.welcomeTitle}>Welcome to the Musuko Team! 🚀</h2>
              <p className={styles.welcomeMessage}>
                Let's build this successful journey together and take your brand to the next level.
              </p>
            </div>
            <div className={styles.redirectSection}>
              <p className={styles.redirectMessage}>
                Redirecting to home page in <span className={styles.countdown}>{countdown}</span> seconds
              </p>
              <button 
                className={styles.redirectButton}
                onClick={() => window.location.href = '/'}
              >
                Click here to go home now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const total = visibleSteps.length;
  const current = visibleSteps[stepIndex];
  const isCurrentStepComplete = current.isComplete
    ? current.isComplete()
    : true;
  const progressPct = Math.round(((stepIndex + 1) / total) * 100);

  return (
    <div className={styles.container}>
      {/* Progress Bar */}
      <div className={styles.progressWrapper}>
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className={styles.progressText}>
          Step {stepIndex + 1} of {total}
        </div>
      </div>

      {/* Step Content */}
      <div className={styles.stepWrapper} key={current.id}>
        {current.render()}
      </div>

      {/* Navigation Buttons */}
      <div className={styles.navButtons}>
        <button
          onClick={() => setStepIndex(i => Math.max(i - 1, 0))}
          disabled={stepIndex === 0}
          className={`${styles.button} ${
            stepIndex === 0 ? styles.disabled : ''
          }`}
        >
          Previous
        </button>

        {stepIndex < total - 1 ? (
          <button
            onClick={() => setStepIndex(i => Math.min(i + 1, total - 1))}
            className={styles.buttonPrimary}
            disabled={!isCurrentStepComplete}
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={sending || !isCurrentStepComplete}
            className={`${styles.buttonPrimary} ${
              sending ? styles.disabled : ''
            }`}
          >
            {sending ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}
